// tslint:disable: member-ordering

import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import mermaid from 'mermaid';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { WorkflowEditorStore } from '../lib/workflow-editor.store';
import { MermaidSerializer } from '../lib/mermaid.serializer';

const MERMAID_ELEMENT_ID = 'mermaid';
type MermaidRendererCallback = (svgGraph: string) => void;

@Component({
    selector: 'eas-workflow-preview',
    templateUrl: './workflow-preview.component.html',
    styleUrls: ['./workflow-preview.component.css'],
    standalone: false
})
export class WorkflowPreviewComponent implements AfterViewInit, OnDestroy {
  public error?: Error;

  /**
   * MermaidJS element ID & Template reference
   *
   * @remarks
   * NOTE: When you change it here, the template reference in TPL must be
   *       changed as well.
   *
   * TODO: How can we replace Angular TPL reference with direct access to
   *       element using ID and get reference for example using getElementByID()?
   */
  private static MERMAID_ELEMENT_ID = MERMAID_ELEMENT_ID;

  private destroyed$ = new Subject<void>();

  private onUpdateMermaid$ = merge(this.store.workflow$, this.store.focusedTransition$)
                             .pipe(takeUntil(this.destroyed$));

  @ViewChild(MERMAID_ELEMENT_ID)
  private mermaidElementRef?: ElementRef;

  // Callback function seems to be required for the mermaid.render() function
  // this is dummy implementation to satisfy the requirement.
  private mermaidRenderCallback: MermaidRendererCallback = () => {};

  constructor(private store: WorkflowEditorStore) { }

  //
  // Lifecycle
  //

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public ngAfterViewInit(): void {
    this.initializeMermaid();

    this.onUpdateMermaid$.subscribe(() => {
      if (!this.store.workflow) { return; }
      const result = MermaidSerializer.serialize(this.store.workflow,
                                                 this.store.focusedTransition);
      if (result.isErr()) {
        this.error = result.error;
        return;
      }

      this.error = undefined;
      this.updateMermaid(result.value, this.mermaidRenderCallback);
    });
  }

  //
  // Mermaid utilities
  //

  private initializeMermaid(): void {
    mermaid.initialize({
      securityLevel: 'loose',
      flowchart: {
        htmlLabels: true,
        curve: 'cardinal'
      },
    });
  }

  private updateMermaid(definition: string, callback: MermaidRendererCallback): void {
    if (!this.mermaidElementRef) { return; }

    this.mermaidElementRef.nativeElement.innerHTML =
      mermaid.render(WorkflowPreviewComponent.MERMAID_ELEMENT_ID,
                     definition,
                     callback);
  }
}
